/**
 * useHermesChat — Manages Skerpa copilot chat state.
 * Context is built from frontend stores (useSkerpaContext).
 * Filters out technical narration from Hermes and shows status indicators instead.
 */

import { useCallback, useEffect } from 'react'
import { nanoid } from 'nanoid'
import { create } from 'zustand'

import { skerpaChatApi } from '@/lib/api/client'
import { useSkerpaContext } from '@/hooks/useSkerpaContext'
import { useSkerpaContextStore } from '@/stores/skerpaContextStore'
import { useProjectStore } from '@/stores/projectStore'
import { useAuthStore } from '@/stores/authStore'
import { useSkerpaStore } from '@/stores/skerpaStore'
import { extractQuestions } from '@/components/hermes/SkerpaMessageContent'
import type { HermesChatMessage, SkerpaAction } from '@ispot/shared'

interface HermesChatState {
  messages: HermesChatMessage[]
  isStreaming: boolean
  streamingText: string
  statusText: string | null
  error: string | null
  activeModel: string | null
}

const useHermesChatStore = create<HermesChatState>(() => ({
  messages: [],
  isStreaming: false,
  streamingText: '',
  statusText: null,
  error: null,
  activeModel: null,
}))

/** Module-level accessor for the chat store (used by auto-greet hook). */
export function getHermesChatState(): HermesChatState {
  return useHermesChatStore.getState()
}

/**
 * Inject a proactive assistant message into chat history (e.g. on view change).
 * Skips if currently streaming. Returns true if message was injected.
 */
export function injectAssistantMessage(content: string): boolean {
  const state = useHermesChatStore.getState()
  if (state.isStreaming) return false
  useHermesChatStore.setState({
    messages: [...state.messages, {
      id: nanoid(),
      role: 'assistant',
      content,
      createdAt: new Date().toISOString(),
    }],
  })
  return true
}

let abortController: AbortController | null = null
let prevViewTitle = ''
let prevProjectId = ''

// Patterns that indicate Hermes is narrating its internal process
const THINKING_PATTERNS = [
  /^Let me (check|look|find|search|verify|see|examine|read|query|get|try|browse|access|use|open)/i,
  /^I('ll| will| need to| see there's| can see|'m going to| should) (check|look|find|search|read|query|try|access|use|browse|verify|examine)/i,
  /^(Now let me|Now I|Checking|Looking at|Searching|Reading|Querying|Accessing|Browsing)/i,
  /^I (see|found|notice) (there's|that|a) .*(route|file|endpoint|schema|table|API|config|database|function|service|module)/i,
  /^The .* has a (complex|session-based|standard|simple) (flow|pattern|structure|implementation)/i,
  /^I have the (API|token|cookie|session|auth)/i,
  /^(Some entities|All entities|The entities) (had|have been|were)/i,
  /\b(localhost|:3002|:5174|\.ts|\.tsx|\.js)\b/,
]

// Extract a user-friendly status from a thinking line
function extractStatus(line: string): string | null {
  if (/creat(e|ing)/i.test(line)) return 'Creating...'
  if (/search|find|look|check|query/i.test(line)) return 'Searching...'
  if (/read|analyz/i.test(line)) return 'Analyzing...'
  if (/verif|valid/i.test(line)) return 'Verifying...'
  if (/updat/i.test(line)) return 'Updating...'
  if (/add/i.test(line)) return 'Adding...'
  return 'Working...'
}

function isThinkingLine(line: string): boolean {
  return THINKING_PATTERNS.some(p => p.test(line.trim()))
}

/** Remove thinking paragraphs from accumulated content, return cleaned + last status */
function filterContent(raw: string): { clean: string; status: string | null } {
  const paragraphs = raw.split('\n\n')
  const cleaned: string[] = []
  let lastStatus: string | null = null

  for (const para of paragraphs) {
    const lines = para.split('\n')
    const isThinking = lines.every(l => !l.trim() || isThinkingLine(l))

    if (isThinking && para.trim()) {
      // Extract status from the first thinking line
      lastStatus = extractStatus(lines.find(l => l.trim()) || '')
    } else {
      cleaned.push(para)
    }
  }

  return { clean: cleaned.join('\n\n').trim(), status: lastStatus }
}

/** Extract <!--actions [...] --> blocks from response text */
function extractActions(content: string): { cleanContent: string; actions: SkerpaAction[] } {
  const actions: SkerpaAction[] = []
  const clean = content.replace(/<!--actions\s*([\s\S]*?)-->/g, (_, json) => {
    try {
      const parsed = JSON.parse(json.trim())
      if (Array.isArray(parsed)) actions.push(...parsed)
    } catch { /* malformed JSON, skip */ }
    return ''
  })
  return { cleanContent: clean.trim(), actions }
}

export function useHermesChat() {
  const messages = useHermesChatStore(s => s.messages)
  const isStreaming = useHermesChatStore(s => s.isStreaming)
  const streamingText = useHermesChatStore(s => s.streamingText)
  const statusText = useHermesChatStore(s => s.statusText)
  const error = useHermesChatStore(s => s.error)
  const activeModel = useHermesChatStore(s => s.activeModel)

  const systemContext = useSkerpaContext()
  const viewTitle = useSkerpaContextStore(s => s.viewTitle)
  const currentProjectId = useProjectStore(s => s.currentProject?.id)
  const skerpaMode = useSkerpaStore(s => s.skerpaMode)
  // Needed by the Composio tool path — backend loads this account's active
  // integrations and exposes their tools to Claude when both IDs are present.
  const currentAccountId = useAuthStore(s => s.currentAccount?.id)
  const currentUserId = useAuthStore(s => s.user?.id)

  // Auto-clear history when project changes — prevents cross-project contamination
  useEffect(() => {
    if (!currentProjectId) return
    const currentMessages = useHermesChatStore.getState().messages
    if (prevProjectId && prevProjectId !== currentProjectId && currentMessages.length > 0) {
      abortController?.abort()
      abortController = null
      useHermesChatStore.setState({
        messages: [],
        streamingText: '',
        statusText: null,
        error: null,
        isStreaming: false,
      })
    }
    prevProjectId = currentProjectId
  }, [currentProjectId])

  // Track view changes — inject context marker into history (hidden from UI, sent to Hermes)
  useEffect(() => {
    if (!viewTitle || viewTitle === prevViewTitle) return
    const currentMessages = useHermesChatStore.getState().messages
    if (prevViewTitle && currentMessages.length > 0) {
      useHermesChatStore.setState({
        messages: [...currentMessages, {
        id: `ctx-${nanoid(6)}`,
        role: 'user' as const,
        content: `[CONTEXT_SWITCH: I navigated to ${viewTitle}. Focus on this context now.]`,
        createdAt: new Date().toISOString(),
        }],
      })
    }
    prevViewTitle = viewTitle
  }, [viewTitle])

  const sendMessage = useCallback(async (text: string) => {
    const state = useHermesChatStore.getState()
    if (!text.trim() || state.isStreaming) return

    const userMsg: HermesChatMessage = {
      id: nanoid(),
      role: 'user',
      content: text.trim(),
      createdAt: new Date().toISOString(),
    }

    useHermesChatStore.setState({
      messages: [...state.messages, userMsg],
      isStreaming: true,
      streamingText: '',
      statusText: null,
      error: null,
    })

    const controller = new AbortController()
    abortController = controller

    try {
      const response = await skerpaChatApi.sendMessage(
        {
          message: text.trim(),
          systemContext,
          history: state.messages.map(m => ({ role: m.role, content: m.content })),
          mode: skerpaMode,
          accountId: currentAccountId,
          userId: currentUserId,
        },
        controller.signal,
      )

      if (!response.ok) {
        const body = await response.text().catch(() => '')
        throw new Error(`Server error: ${response.status} ${body}`)
      }

      if (!response.body) throw new Error('No response body')

      let rawAccumulated = ''
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed || !trimmed.startsWith('data:')) continue

          const dataStr = trimmed.slice(5).trim()
          try {
            const data = JSON.parse(dataStr)
            if (data.provider && data.model && !data.content) {
              // Meta event — capture active model
              useHermesChatStore.setState({ activeModel: data.model })
            }
            if (data.content) {
              rawAccumulated += data.content
              const { clean, status } = filterContent(rawAccumulated)
              // Strip action blocks from streaming display
              const display = clean.replace(/<!--actions[\s\S]*?-->/g, '').trim()
              useHermesChatStore.setState({
                streamingText: display,
                statusText: status || (clean ? null : useHermesChatStore.getState().statusText),
              })
            }
            if (data.message) {
              useHermesChatStore.setState({ error: data.message })
            }
            // Tool-use surface: when the coach dispatches a Composio tool,
            // show a transient status so the user sees real action happening.
            if (data.toolName) {
              const ok = data.toolOutput?.ok !== false
              useHermesChatStore.setState({
                statusText: ok
                  ? `Called ${data.toolName}`
                  : `Tool ${data.toolName} failed`,
              })
            }
          } catch { /* skip */ }
        }
      }

      // Final filter + extract actions + detect interview questions
      const { clean } = filterContent(rawAccumulated)
      const { cleanContent, actions } = extractActions(clean)
      const questions = extractQuestions(cleanContent)
      if (questions.length >= 2 && !actions.some(a => a.action === 'interview')) {
        actions.push({ action: 'interview', questions })
      }
      if (cleanContent) {
        const latestMessages = useHermesChatStore.getState().messages
        useHermesChatStore.setState({ messages: [...latestMessages, {
          id: nanoid(),
          role: 'assistant',
          content: cleanContent,
          actions: actions.length > 0 ? actions : undefined,
          createdAt: new Date().toISOString(),
        }] })
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        useHermesChatStore.setState({ error: err instanceof Error ? err.message : 'Failed to send message' })
      }
    } finally {
      useHermesChatStore.setState({
        isStreaming: false,
        streamingText: '',
        statusText: null,
      })
      abortController = null
    }
  }, [systemContext, skerpaMode, currentAccountId, currentUserId])

  const clearHistory = useCallback(() => {
    if (abortController) abortController.abort()
    abortController = null
    useHermesChatStore.setState({
      messages: [],
      streamingText: '',
      statusText: null,
      error: null,
      isStreaming: false,
    })
  }, [])

  const stopStreaming = useCallback(() => {
    if (abortController) abortController.abort()
  }, [])

  return { messages, isStreaming, streamingText, statusText, error, activeModel, sendMessage, clearHistory, stopStreaming }
}
